import React from 'react'
import { SESSION_INTERFACE_IDS } from '../../domain'
import { RTC_PROVIDERS } from '../../providerIds'
import { getRtcProviderCandidateIds } from '../../providers/rtc'
import { DailyRtcCompatibilitySurface } from '../../providers/rtc/daily'
import { HundredMsRtcCompatibilitySurface } from '../../providers/rtc/hundredMsRtcCompat'
import { useRtcSessionShell } from '../hooks/useRtcSessionShell'
import {
  buildDailyRtcProps,
  buildHundredMsRtcProps,
} from '../rtcSessionShellSupport'
import { useRtcSessionHandleConfig } from '../useRtcSessionHandleConfig'
import SharedAgoraVideoCallSurface from './SharedAgoraVideoCallSurface'
import SharedVideoCallShell from '../../ui/SharedVideoCallShell'

const AGORA_LOCAL_TOGGLE_STATE = Object.freeze({
  disabling: 2,
  enabling: 3,
})

export default function SharedRtcVideoSurface({
  sessionRoutingDecision,
  agoraRtcProps,
  agoraRtmProps,
  handleLeaveRoom,
  localParticipantIdentity = null,
  peerParticipantHint = null,
  sessionInterfaceId = SESSION_INTERFACE_IDS.standardLive,
  onUserJoin,
  onUserLeave,
  onCameraClose,
  onCameraOpen,
  onAudioClose,
  onAudioOpen,
  providerFallback = null,
}) {
  const candidateProviderIds = React.useMemo(
    () => getRtcProviderCandidateIds(sessionRoutingDecision),
    [sessionRoutingDecision],
  )
  const hundredMsRtcProps = React.useMemo(
    () =>
      buildHundredMsRtcProps(
        sessionRoutingDecision,
        agoraRtcProps,
        localParticipantIdentity,
      ),
    [agoraRtcProps, localParticipantIdentity, sessionRoutingDecision],
  )
  const dailyRtcProps = React.useMemo(
    () =>
      buildDailyRtcProps(
        sessionRoutingDecision,
        agoraRtcProps,
        localParticipantIdentity,
      ),
    [agoraRtcProps, localParticipantIdentity, sessionRoutingDecision],
  )
  const [failedProviderIds, setFailedProviderIds] = React.useState([])
  const candidateProviderIdsKey = React.useMemo(
    () => candidateProviderIds.join('|'),
    [candidateProviderIds],
  )

  React.useEffect(() => {
    setFailedProviderIds([])
  }, [
    candidateProviderIdsKey,
    dailyRtcProps?.token,
    dailyRtcProps?.roomUrl,
    dailyRtcProps?.userId,
    dailyRtcProps?.userName,
    hundredMsRtcProps?.authToken,
    hundredMsRtcProps?.roomId,
    agoraRtcProps?.appId,
    agoraRtcProps?.channel,
    agoraRtcProps?.uid,
    localParticipantIdentity?.participantId,
  ])

  const activeProviderId = candidateProviderIds.find((providerId) => {
    if (failedProviderIds.includes(providerId)) {
      return false
    }

    if (providerId === RTC_PROVIDERS.hundredMs) {
      return Boolean(hundredMsRtcProps)
    }

    if (providerId === RTC_PROVIDERS.dailyCo) {
      return Boolean(dailyRtcProps)
    }

    if (providerId === RTC_PROVIDERS.agora) {
      return Boolean(agoraRtcProps?.appId)
    }

    return false
  })
  const providerInitialConfig = React.useMemo(() => {
    if (activeProviderId === RTC_PROVIDERS.hundredMs) {
      return {
        rtcProps: hundredMsRtcProps || {},
      }
    }

    if (activeProviderId === RTC_PROVIDERS.dailyCo) {
      return {
        rtcProps: dailyRtcProps || {},
      }
    }

    return {
      rtcProps: agoraRtcProps || {},
      rtmProps: agoraRtmProps || null,
    }
  }, [activeProviderId, agoraRtcProps, agoraRtmProps, dailyRtcProps, hundredMsRtcProps])
  const shell = useRtcSessionShell({
    providerId: activeProviderId || RTC_PROVIDERS.agora,
    sessionKey: [
      activeProviderId || 'none',
      activeProviderId === RTC_PROVIDERS.dailyCo
        ? dailyRtcProps?.roomUrl || ''
        : agoraRtcProps?.channel || hundredMsRtcProps?.roomId || '',
      activeProviderId === RTC_PROVIDERS.dailyCo
        ? dailyRtcProps?.userId || dailyRtcProps?.userName || ''
        : agoraRtcProps?.uid ||
          hundredMsRtcProps?.userId ||
          hundredMsRtcProps?.userName ||
          '',
    ].join(':'),
    surfacePolicyId: sessionInterfaceId,
    localParticipantIdentity,
    providerInitialConfig,
  })
  const compatibilityConfig = useRtcSessionHandleConfig(shell.sessionHandle)
  const resolvedAgoraRtcProps = compatibilityConfig?.rtcProps || agoraRtcProps
  const resolvedAgoraRtmProps = compatibilityConfig?.rtmProps || agoraRtmProps
  const resolvedHundredMsRtcProps = compatibilityConfig?.rtcProps || hundredMsRtcProps
  const resolvedDailyRtcProps = compatibilityConfig?.rtcProps || dailyRtcProps
  const agoraCallbacks = React.useMemo(
    () => ({
      EndCall: () => {
        handleLeaveRoom?.()
      },
      'user-joined': (user) => {
        onUserJoin?.(user)
      },
      'user-left': (user) => {
        onUserLeave?.(user)
      },
      'local-user-mute-video': (state) => {
        if (state === AGORA_LOCAL_TOGGLE_STATE.disabling) {
          onCameraClose?.()
          return
        }

        if (state === AGORA_LOCAL_TOGGLE_STATE.enabling) {
          onCameraOpen?.()
        }
      },
      'local-user-mute-audio': (state) => {
        if (state === AGORA_LOCAL_TOGGLE_STATE.disabling) {
          onAudioClose?.()
          return
        }

        if (state === AGORA_LOCAL_TOGGLE_STATE.enabling) {
          onAudioOpen?.()
        }
      },
    }),
    [
      handleLeaveRoom,
      onAudioClose,
      onAudioOpen,
      onCameraClose,
      onCameraOpen,
      onUserJoin,
      onUserLeave,
    ],
  )
  const sharedShellElement = (
    <SharedVideoCallShell
      shell={shell}
      sessionInterfaceId={sessionInterfaceId}
      peerParticipantHint={peerParticipantHint}
      handleLeaveRoom={handleLeaveRoom}
      providerFallback={providerFallback}
    />
  )

  if (activeProviderId === RTC_PROVIDERS.hundredMs && resolvedHundredMsRtcProps) {
    return (
      <HundredMsRtcCompatibilitySurface
        renderless
        rtcProps={resolvedHundredMsRtcProps}
        callbacks={{
          onLeave: handleLeaveRoom,
          onUserJoin,
          onUserLeave,
          onCameraStateChange: (enabled) => {
            if (enabled) {
              onCameraOpen?.()
              return
            }

            onCameraClose?.()
          },
          onAudioStateChange: (enabled) => {
            if (enabled) {
              onAudioOpen?.()
              return
            }

            onAudioClose?.()
          },
          'participants-updated': shell.handleParticipantSnapshot,
          'connection-state-change': shell.handleConnectionStateChange,
        }}
        onProviderError={() => {
          setFailedProviderIds((previousProviderIds) => {
            if (previousProviderIds.includes(RTC_PROVIDERS.hundredMs)) {
              return previousProviderIds
            }

            return [...previousProviderIds, RTC_PROVIDERS.hundredMs]
          })
        }}
      >
        {sharedShellElement}
      </HundredMsRtcCompatibilitySurface>
    )
  }

  if (activeProviderId === RTC_PROVIDERS.dailyCo && resolvedDailyRtcProps) {
    return (
      <DailyRtcCompatibilitySurface
        renderless
        rtcProps={resolvedDailyRtcProps}
        callbacks={{
          onLeave: handleLeaveRoom,
          onUserJoin,
          onUserLeave,
          onCameraStateChange: (enabled) => {
            if (enabled) {
              onCameraOpen?.()
              return
            }

            onCameraClose?.()
          },
          onAudioStateChange: (enabled) => {
            if (enabled) {
              onAudioOpen?.()
              return
            }

            onAudioClose?.()
          },
          'participants-updated': shell.handleParticipantSnapshot,
          'connection-state-change': shell.handleConnectionStateChange,
        }}
        onProviderError={() => {
          setFailedProviderIds((previousProviderIds) => {
            if (previousProviderIds.includes(RTC_PROVIDERS.dailyCo)) {
              return previousProviderIds
            }

            return [...previousProviderIds, RTC_PROVIDERS.dailyCo]
          })
        }}
      >
        {sharedShellElement}
      </DailyRtcCompatibilitySurface>
    )
  }

  if (resolvedAgoraRtcProps?.appId) {
    return (
      <SharedAgoraVideoCallSurface
        shell={shell}
        rtcProps={resolvedAgoraRtcProps}
        rtmProps={resolvedAgoraRtmProps}
        handleLeaveRoom={handleLeaveRoom}
        fallback={providerFallback}
        peerParticipantHint={peerParticipantHint}
        callbacks={agoraCallbacks}
      />
    )
  }

  return providerFallback
}

SharedRtcVideoSurface.displayName = 'SharedRtcVideoSurface'
